<template>
  <v-container fluid grid-list-md>
    <v-row row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="pb-0">
            <h2 class="display-1 ml-4">Poi</h2>
            <v-spacer/>
            <v-btn color="accent" class="secondary--text" @click="$router.push('/poi/new')">New</v-btn>
          </v-card-title>
          <v-card-text>
            <v-container fluid grid-list-md>
              <v-row row wrap>
                <v-col cols="12" sm="6" md="4" lg="3">
                  <v-autocomplete filled rounded dense hide-details v-model="photoSearch" clearable
                                  :items="photoOptions" label="Search by photo" @change="applyFilter"></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6" md="4" lg="3">
                  <v-autocomplete filled rounded dense hide-details v-model="mediaSearch" clearable
                                  :items="mediaOptions" label="Search by media" @change="applyFilter"></v-autocomplete>
                </v-col>
              </v-row>
              <v-row row wrap>

                <v-col cols="12">
                  <v-data-table :headers="headers" :items="items" :options.sync="pagination"
                                :server-items-length="count" :loading="tableLoading"
                                no-data-text="There are no items yet" :footer-props="footerProps">
                    <template v-slot:item.media.name="{ item }">
                      <a v-if="item.media" :href="'./#/media/'+item.media._id"
                         :title="item.media.name"><b>{{ item.media.name }}</b></a>
                    </template>
                    <template v-slot:item.photo.title.en="{ item }">
                      <a v-if="item.photo" :href="'./#/photos/'+item.photo._id"
                         :title="item.photo.title.en"><b>{{ item.photo.title.en }}</b></a>
                    </template>
                    <template v-slot:item.location="{ item }">
                      lat: {{ item.location.coordinates[1] }}, lng: {{ item.location.coordinates[0] }}
                    </template>
                    <template v-slot:item.pub="{ item }">
                      <strong class="success--text" v-if="item.pub">Yes</strong>
                      <strong class="error--text" v-else>No</strong>
                    </template>
                    <template v-slot:item.edit="{ item }">
                      <v-btn color="primary" dark text icon
                             v-on:click="$router.push({ name : 'poi form', params : { id : item._id }})">
                        <v-icon dark>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <template v-slot:item.remove="{ item }">
                      <v-btn color="error" dark text icon v-on:click="requestDelete(item._id)">
                        <v-icon dark>mdi-close</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

  </v-container>
</template>

<script>
import {mapGetters, mapActions} from 'vuex';
import config from "../config";
import utils from "../utils";
import _ from "lodash";
import log from "@/log";

export default {
  data() {
    return {
      refreshKey: 0,
      refreshTimeout: -1,
      photoSearch: null,
      mediaSearch: null,
      tableLoading: false,
      count: 0,
      footerProps: {
        "items-per-page-text": "no. items per page",
        "items-per-page-options": [20, 50, 100],
        "items-per-page": 20
      },
      items: [],
      pagination: {},
      query: {},
    }
  },
  watch: {
    pagination: {
      handler() {
        sessionStorage.poiPagination = JSON.stringify(this.pagination);
        clearTimeout(this.refreshTimeout);
        this.refreshTimeout = setTimeout(this.refresh, 250);
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters(['is_admin', 'photos', 'media']),
    server() {
      return config.server;
    },
    headers() {
      return [
        {text: 'Location', sortable: false, align: 'left', value: 'location'},
        {text: 'Photo', sortable: true, align: 'left', value: 'photo.title.en'},
        {text: 'Media', sortable: true, align: 'left', value: 'media.name'},
        {text: 'Public', sortable: true, align: 'center', value: 'pub', width: 50},
        {text: 'Edit', sortable: false, align: 'center', value: 'edit', width: 50},
        {text: 'Delete', sortable: false, align: 'center', value: 'remove', width: 50},
      ]
    },
    photoOptions() {
      return this.photos.map((item) => {
        return {text: item.title.en || '-', value: item._id}
      }).sort(utils.sortTextOn('text'))
    },
    mediaOptions() {
      return this.media.map((item) => {
        return {text: item.name || '-', value: item._id}
      }).sort(utils.sortTextOn('text'))
    },
  },
  methods: {
    ...mapActions(['get', 'sendSuccess', 'sendError', 'requestConfirm', 'logout', 'load', 'delete', 'update', 'refreshCollection']),
    applyFilter() {
      this.query = {};
      this.query.photo = this.photoSearch ? this.photoSearch : undefined;
      this.query.media = this.mediaSearch ? this.mediaSearch : undefined;
      this.pagination.page = 1
      sessionStorage.poiQuery = JSON.stringify(this.query);
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = setTimeout(this.refresh, 250);
    },
    requestDelete(id) {
      this.requestConfirm({
        title: 'Delete request', message: 'Are you sure to permanently delete this item?', callback: (accepted) => {
          if (accepted) {
            this.delete({collection: 'poi', id}).then(() => {
              this.refresh();
              this.sendSuccess({message: 'Item deleted!'});
            }).catch((err) => {
              let message = err.message ? err.message : "there was an error and the request could not be completed";
              this.sendError({message});
              log.d("there was an error while removing poi", err);
              if (err.statusCode === 401) {
                this.logout();
              }
            })

          }
        }
      });
    },
    refresh() {
      this.tableLoading = true;
      const {sortBy, sortDesc, page, itemsPerPage} = this.pagination;
      let sort = null;
      if (sortBy && sortBy.length) {
        sort = sortBy[0];
        if (sortDesc && sortDesc[0]) {
          sort = encodeURIComponent('-' + sort);
        } else {
          sort = encodeURIComponent('+' + sort);
        }
      }

      let filter = {
        query: this.query,
        limit: itemsPerPage,
        page,
        sort
      };

      this.load({collection: 'poi', filter}).then((reply) => {
        this.tableLoading = false;
        if (reply.status === "success") {
          this.count = reply.count;
          this.items = reply.data ? reply.data : [];
        }
      }).catch((err) => {
        this.tableLoading = false;
        this.sendError({message: "there was an error while loading data"});
        log.d("there was an error while loading poi: " + err);
        if (err.statusCode === 401) {
          this.logout();
        }
      })
    }
  },
  mounted() {
    if (sessionStorage.poiQuery) {
      this.query = JSON.parse(sessionStorage.poiQuery);
      if (this.query.title) {
        this.nameSearch = this.query.title
      }
    }
    if (sessionStorage.poiPagination) {
      this.pagination = JSON.parse(sessionStorage.poiPagination);
    }
    clearTimeout(this.refreshTimeout);
    this.refreshTimeout = setTimeout(this.refresh, 250);

    this.refreshCollection({collection: 'photos'});
    this.refreshCollection({collection: 'media'});
  }
}
</script>
